import { VERSES, type VerseEntry, findVerse } from '@rb/shared/admin-verses';
import { CheckIcon, CopyIcon, Loader2Icon, MinusIcon, XIcon } from 'lucide-react';
import { useState } from 'react';
import { SeedClient } from '@/client/seed.client';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { selectEffectiveGatewayId, useActiveVerseStore } from '@/verse/active-verse.store';
import type { EnvSchema } from './EnvSelector';
import { seedConnectionUrls } from './seed-gateway';
import { buildR2Url } from './lib/collection-sources';
import { callEdgeFunction } from './supabase.client';

interface UploadedFile {
  key: string;
  size: number;
}

interface ApplyResp {
  ok: boolean;
  schema: EnvSchema;
  version: string;
  uploaded: UploadedFile[];
  client_collections: string[];
  server_collections: string[];
}

type PhaseStatus = 'pending' | 'running' | 'ok' | 'error' | 'skipped';

interface PhaseState {
  status: PhaseStatus;
  error?: string;
}

const PROD_CONFIRM = 'PROD 데이터 적용';

/** admin EnvSchema ('production') → VerseEnv ('prod'); else identity. */
function verseEnvForEnvSchema(env: EnvSchema): 'dev' | 'staging' | 'prod' {
  return env === 'production' ? 'prod' : env;
}

/** Which V8 verse(s) the seed step targets. */
type SeedTarget = 'web2' | 'web3' | 'both';

interface Props {
  env: EnvSchema;
  onClose: () => void;
}

export function ApplyPanel({ env, onClose }: Props) {
  const [confirmText, setConfirmText] = useState('');
  const [busy, setBusy] = useState(false);
  const [seedTarget, setSeedTarget] = useState<SeedTarget>('both');

  const [r2Phase, setR2Phase] = useState<PhaseState>({ status: 'pending' });
  // Per-verse V8 seeding status (keyed by verse alias).
  const [seedPhases, setSeedPhases] = useState<Record<string, PhaseState>>({});
  const [donePhase, setDonePhase] = useState<PhaseState>({ status: 'pending' });

  const [applyResp, setApplyResp] = useState<ApplyResp | null>(null);

  const needsConfirm = env === 'production';
  const verseEnv = verseEnvForEnvSchema(env);
  const envVerses = VERSES.filter((v) => v.env === verseEnv);

  function targetVerses(): VerseEntry[] {
    if (seedTarget === 'both') return envVerses;
    return envVerses.filter((v) => v.flavor === seedTarget);
  }

  function reset() {
    setR2Phase({ status: 'pending' });
    setSeedPhases({});
    setDonePhase({ status: 'pending' });
    setApplyResp(null);
  }

  function setSeedPhase(alias: string, state: PhaseState) {
    setSeedPhases((prev) => ({ ...prev, [alias]: state }));
  }

  /** Phase 1 — build + upload collections to R2 via the apply EF. */
  async function runR2(): Promise<ApplyResp> {
    setR2Phase({ status: 'running' });
    const apply = await callEdgeFunction<ApplyResp>('apply', { schema: env });
    setApplyResp(apply);
    setR2Phase({ status: 'ok' });
    return apply;
  }

  /**
   * Phase 2 — seed the selected verse(s) SEQUENTIALLY. Concurrent seeding of
   * web2/web3 trips token-dupe (4009) on the shared GameServer token, so we
   * switch the active verse, await the seed, then move to the next.
   */
  async function runV8Seed(targets: VerseEntry[]): Promise<void> {
    const state = useActiveVerseStore.getState();
    const { manager, setActive } = state;
    if (!manager) throw new Error('verse 연결 없음 — 먼저 verse 에 접속하세요');
    const originalVerseId = state.activeVerseId;
    const { seedUrl: seedGatewayUrl, restoreUrl: originalGatewayUrl } = seedConnectionUrls(
      verseEnv,
      selectEffectiveGatewayId(state)
    );

    // initialize statuses
    for (const v of targets) setSeedPhase(v.alias, { status: 'pending' });

    try {
      for (const v of targets) {
        setSeedPhase(v.alias, { status: 'running' });
        // Switch active verse + reconnect, then seed. Sequential by await.
        await manager.switchTo(v.alias, seedGatewayUrl);
        setActive(v.verseId);
        await SeedClient.seedFromAssets(env);
        setSeedPhase(v.alias, { status: 'ok' });
      }
    } finally {
      // Restore the originally-active verse + its gateway so the UI selection is unchanged.
      if (originalVerseId) {
        const ov = findVerse(originalVerseId);
        if (ov) {
          await manager.switchTo(ov.alias, originalGatewayUrl).catch(() => {
            /* best-effort restore — ignore reconnect failure */
          });
          setActive(ov.verseId);
        }
      }
    }
  }

  function guardConfirm(): boolean {
    if (needsConfirm && confirmText !== PROD_CONFIRM) {
      setR2Phase({ status: 'error', error: '컨펌 텍스트 불일치' });
      return false;
    }
    return true;
  }

  /** R2 upload only — skip V8 seeding. */
  async function runR2Only() {
    if (!guardConfirm()) return;
    reset();
    setBusy(true);
    try {
      await runR2();
      setDonePhase({ status: 'ok' });
    } catch (e) {
      const msg = (e as Error).message;
      setR2Phase((p) => (p.status === 'ok' ? p : { status: 'error', error: msg }));
      setDonePhase({ status: 'error', error: msg });
    } finally {
      setBusy(false);
    }
  }

  /** Full apply — R2 upload then V8 seed to the selected verse(s). */
  async function runFull() {
    if (!guardConfirm()) return;
    const targets = targetVerses();
    if (targets.length === 0) {
      setR2Phase({ status: 'error', error: `${env} verse 없음` });
      return;
    }
    reset();
    setBusy(true);
    try {
      await runR2();
      await runV8Seed(targets);
      setDonePhase({ status: 'ok' });
    } catch (e) {
      const msg = (e as Error).message;
      setR2Phase((p) => (p.status === 'ok' ? p : { status: 'error', error: msg }));
      setDonePhase({ status: 'error', error: msg });
    } finally {
      setBusy(false);
    }
  }

  /** V8 seed only — skip R2 upload (uses whatever is already on R2/assets). */
  async function runSeedOnly() {
    if (!guardConfirm()) return;
    const targets = targetVerses();
    if (targets.length === 0) {
      setR2Phase({ status: 'error', error: `${env} verse 없음` });
      return;
    }
    reset();
    setR2Phase({ status: 'skipped' });
    setBusy(true);
    try {
      await runV8Seed(targets);
      setDonePhase({ status: 'ok' });
    } catch (e) {
      const msg = (e as Error).message;
      setDonePhase({ status: 'error', error: msg });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>Apply to {env}</DialogTitle>
        </DialogHeader>

        {needsConfirm && (
          <div className="flex flex-col gap-2 rounded-md border border-rose-300/60 bg-rose-50 p-3 text-sm dark:border-rose-900/60 dark:bg-rose-950/30">
            <span className="font-medium text-rose-700 dark:text-rose-300">
              ⚠ PRODUCTION 적용. 아래 정확히 입력:
            </span>
            <code className="rounded bg-muted px-2 py-1 text-xs">{PROD_CONFIRM}</code>
            <Label htmlFor="md-prod-confirm" className="sr-only">
              컨펌 텍스트
            </Label>
            <Input
              id="md-prod-confirm"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="컨펌 텍스트"
            />
          </div>
        )}

        {/* V8 seed target verse selector (R2 upload is per-env, verse-agnostic). */}
        <div className="flex flex-col gap-1.5 rounded-md border border-border bg-card px-3 py-2">
          <span className="text-xs font-medium text-foreground">V8 시딩 대상 verse</span>
          <div className="flex gap-2">
            {(['web2', 'web3', 'both'] as const).map((opt) => {
              const label = opt === 'both' ? '둘 다 (순차)' : opt === 'web2' ? 'Web2' : 'Web3';
              const disabled = busy || (opt !== 'both' && !envVerses.some((v) => v.flavor === opt));
              return (
                <Button
                  key={opt}
                  type="button"
                  size="sm"
                  variant={seedTarget === opt ? 'default' : 'outline'}
                  disabled={disabled}
                  onClick={() => setSeedTarget(opt)}
                >
                  {label}
                </Button>
              );
            })}
          </div>
          <span className="text-xs text-muted-foreground">
            대상:{' '}
            {targetVerses()
              .map((v) => v.label)
              .join(' → ') || '(없음)'}{' '}
            · 동시 시딩은 토큰 중복(4009)으로 실패하므로 순차 실행
          </span>
        </div>

        <div className="flex flex-col gap-2">
          <PhaseRow label="R2 upload" state={r2Phase} />
          {targetVerses().map((v) => (
            <PhaseRow
              key={v.alias}
              label={`V8 시딩 · ${v.label}`}
              state={seedPhases[v.alias] ?? { status: 'pending' }}
            />
          ))}
          <PhaseRow label="Done" state={donePhase} />
        </div>

        {applyResp && <UploadedFilesTable env={env} apply={applyResp} />}

        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="outline" onClick={onClose} disabled={busy}>
            닫기
          </Button>
          <Button type="button" variant="outline" onClick={runR2Only} disabled={busy}>
            {busy ? '진행 중…' : 'R2 업로드만'}
          </Button>
          <Button type="button" variant="outline" onClick={runSeedOnly} disabled={busy}>
            {busy ? '진행 중…' : 'V8 시딩만'}
          </Button>
          <Button type="button" onClick={runFull} disabled={busy}>
            {busy ? '진행 중…' : 'R2 + V8 시딩'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function PhaseRow({ label, state }: { label: string; state: PhaseState }) {
  const Icon =
    state.status === 'running'
      ? Loader2Icon
      : state.status === 'ok'
        ? CheckIcon
        : state.status === 'error'
          ? XIcon
          : MinusIcon;
  const tone =
    state.status === 'ok'
      ? 'text-emerald-600 dark:text-emerald-400'
      : state.status === 'error'
        ? 'text-rose-600 dark:text-rose-400'
        : state.status === 'running'
          ? 'text-amber-600 dark:text-amber-400'
          : 'text-muted-foreground';
  return (
    <div className="flex items-center gap-2 rounded-md border border-border bg-card px-3 py-2 text-sm">
      <Icon className={`size-4 ${tone} ${state.status === 'running' ? 'animate-spin' : ''}`} />
      <span className="font-medium text-foreground">{label}</span>
      {state.error && <span className={`ml-auto text-xs ${tone}`}>{state.error}</span>}
      {!state.error && state.status === 'skipped' && (
        <span className="ml-auto text-xs text-muted-foreground">skipped</span>
      )}
    </div>
  );
}

function UploadedFilesTable({ env, apply }: { env: EnvSchema; apply: ApplyResp }) {
  const [copied, setCopied] = useState<string | null>(null);
  return (
    <div className="rounded-md border border-border">
      <div className="border-b border-border bg-muted/40 px-3 py-2 text-xs">
        <span className="font-medium text-foreground">
          R2 uploaded ({apply.uploaded.length} files, version {apply.version})
        </span>
      </div>
      <div className="max-h-[40vh] overflow-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-xs">Key</TableHead>
              <TableHead className="text-right text-xs">Size (bytes)</TableHead>
              <TableHead className="text-xs">R2 URL</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {apply.uploaded.map((u) => {
              const url = buildR2Url(env, u.key, apply.version);
              return (
                <TableRow key={u.key}>
                  <TableCell className="font-mono text-xs">{u.key}</TableCell>
                  <TableCell className="text-right font-mono text-xs">
                    {u.size.toLocaleString()}
                  </TableCell>
                  <TableCell className="text-xs">
                    <div className="flex items-center gap-2">
                      <a
                        href={url}
                        target="_blank"
                        rel="noreferrer"
                        className="break-all font-mono text-primary hover:underline"
                      >
                        {url}
                      </a>
                      <Button
                        type="button"
                        size="icon-xs"
                        variant="outline"
                        onClick={async () => {
                          await navigator.clipboard.writeText(url);
                          setCopied(u.key);
                          setTimeout(() => setCopied((c) => (c === u.key ? null : c)), 1200);
                        }}
                        aria-label="Copy URL"
                      >
                        {copied === u.key ? (
                          <CheckIcon className="size-3" />
                        ) : (
                          <CopyIcon className="size-3" />
                        )}
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
